import React, { useCallback, useContext, useMemo } from "react";
import { Link, LinkProps, useLocation, useNavigate } from "react-router-dom";

import {
  useCurrentComparePlan,
  useCurrentPlan,
} from "features/Explain/WithExplainPlan";

import { Node, Annotation } from "types/explain";
import { findNode, isReferencedBy } from "utils/explain";

type PlanCompareKind = "main" | "comparison";

type PlanContext = {
  selectedNodeId: number | string;
  NodeLink: React.ComponentType<NodeLinkProps>;
  selectedNode: Node | undefined;
  selectedAnnotationId: number | undefined;
  AnnotationLink: React.ComponentType<AnnotationLinkProps>;
  selectedAnnotation: Annotation;
  selection: "node" | "annotation";
};

type NodeLinkProps = { nodeId: string; kind: PlanCompareKind } & Omit<
  LinkProps,
  "to"
>;
type AnnotationLinkProps = { annotationId: number } & Omit<LinkProps, "to">;

const SelectedNodeContext = React.createContext<PlanContext | undefined>(
  undefined,
);

/**
 * Allows selecting a single node in an explain plan, and optionally, focusing
 * the plan on one or more nodes via annotations.
 */
const WithNodeSelection = ({ children }: { children: React.ReactNode }) => {
  const plan = useCurrentPlan();
  const comparePlan = useCurrentComparePlan();
  const { hash, search } = useLocation();

  const referencesComparisonPlan = hash.startsWith("#compare-");

  const hashNodeMatch =
    hash.length > 0 && hash.match(/^#(?:compare-)?node-(.+)$/);
  const hashNodeId = hashNodeMatch ? hashNodeMatch[1] : undefined;

  const hashAnnotationMatch =
    hash.length > 0 && hash.match(/^#(?:compare-)?annotation-(\d+)$/);
  const hashAnnotationId = hashAnnotationMatch
    ? parseInt(hashAnnotationMatch[1], 10)
    : undefined;

  const nodePlan = referencesComparisonPlan ? comparePlan : plan;

  const currentValue = useMemo(() => {
    const hashNode = findNode(nodePlan?.plan.plan, hashNodeId);

    // Find selected annotation, or annotation that applies to selected node,
    let selectedAnnotationId = hashAnnotationId;
    if (selectedAnnotationId === undefined) {
      const annIdx = nodePlan?.plan.annotations.findIndex((ann) => {
        return isReferencedBy(hashNode?.extra.id, ann);
      });
      if (annIdx >= 0) {
        selectedAnnotationId = annIdx;
      }
    }

    const selectedAnnotation = nodePlan?.plan.annotations.find(
      (_, idx) => idx === selectedAnnotationId,
    );

    // Then find selected node, or the first node from the selected annotation
    const selectedNodeId =
      hashNodeId ?? selectedAnnotation?.nodes.at(0)?.id.toString();
    const selectedNode =
      hashNode ?? findNode(nodePlan?.plan.plan, selectedNodeId);

    return {
      selectedNodeId,
      NodeLink: ({ nodeId, kind = "main", ...rest }: NodeLinkProps) => {
        const hash =
          nodeId == null
            ? ""
            : kind === "main"
            ? `#node-${nodeId}`
            : `#compare-node-${nodeId}`;
        return <Link to={{ hash, search }} {...rest} />;
      },
      selectedNode,
      selectedAnnotationId,
      AnnotationLink: ({ annotationId, ...rest }: AnnotationLinkProps) => {
        const hash = annotationId == null ? "" : `#annotation-${annotationId}`;
        return <Link to={{ hash, search }} {...rest} />;
      },
      selectedAnnotation,
      selection: hashNodeId ? ("node" as const) : ("annotation" as const),
    };
  }, [hashNodeId, hashAnnotationId, search, nodePlan]);
  return (
    <SelectedNodeContext.Provider value={currentValue}>
      {children}
    </SelectedNodeContext.Provider>
  );
};

export function useSelectedNode() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const setSelectedNodeId = useCallback(
    (nodeId: number | string, kind: PlanCompareKind = "main") => {
      const hash =
        nodeId == null
          ? ""
          : kind === "main"
          ? `#node-${nodeId}`
          : `#compare-node-${nodeId}`;
      navigate({ hash, search });
    },
    [navigate, search],
  );
  const { selectedNode } = useContext(SelectedNodeContext);

  return [selectedNode, setSelectedNodeId] as const;
}

export function useNodeLink() {
  const { NodeLink } = useContext(SelectedNodeContext);

  return NodeLink;
}

export function useSelectedAnnotationId() {
  const { selectedAnnotationId } = useContext(SelectedNodeContext);

  return selectedAnnotationId;
}

export function useSetSelectedAnnotationId(opts?: { replace?: boolean }) {
  const navigate = useNavigate();
  const { search } = useLocation();
  const replace = opts?.replace;
  const setSelectedAnnotationId = useCallback(
    (annotationId: number) => {
      const hash = annotationId == null ? "" : `#annotation-${annotationId}`;
      navigate({ search, hash }, { replace });
    },
    [navigate, search, replace],
  );

  return setSelectedAnnotationId;
}

export function useSelectedAnnotation() {
  const setter = useSetSelectedAnnotationId();
  const { selectedAnnotation } = useContext(SelectedNodeContext);

  return [selectedAnnotation, setter] as const;
}

export function useAnnotationLink() {
  const { AnnotationLink } = useContext(SelectedNodeContext);

  return AnnotationLink;
}

export function useExplainSelection() {
  const { selection } = useContext(SelectedNodeContext);

  return selection;
}

export default WithNodeSelection;
